import {
  format as dateFnsFormat,
  startOfWeek,
  addWeeks,
  subWeeks,
  parseISO,
} from "date-fns";

import { DATE_FMT, DATE_FMT_INTERNAL } from "@wc/domain/constants";

/**
 * The start of the work week, aka MONDAY
 * @returns {Date} Last Monday
 */
export function lastMonday() {
  return startOfWeek(new Date(), { weekStartsOn: 1 });
}

/**
 * The Wincardsapp string date format for display
 * @param {Date} date The date to format
 * @returns {string} The formatted date
 */
export function formatDate(date) {
  return dateFnsFormat(date, DATE_FMT);
}

/**
 * The internal string representation of dates
 * @param {Date} date The date to format
 * @returns {string} The formatted date
 */
export function formatDateInternal(date) {
  return dateFnsFormat(date, DATE_FMT_INTERNAL);
}

/**
 * Get current week Monday date key (yyyy-MM-dd)
 * @returns {string} The current week key
 */
export function getCurrentWeekKey() {
  return formatDateInternal(lastMonday());
}

/**
 * Get previous week Monday date key
 * @param {string} weekOfKey Week key in yyyy-MM-dd format
 * @returns {string} The previous week key
 */
export function getPreviousWeekKey(weekOfKey) {
  const date = parseISO(weekOfKey);
  return formatDateInternal(subWeeks(date, 1));
}

/**
 * Get next week Monday date key
 * @param {string} weekOfKey Week key in yyyy-MM-dd format
 * @returns {string} The next week key
 */
export function getNextWeekKey(weekOfKey) {
  const date = parseISO(weekOfKey);
  return formatDateInternal(addWeeks(date, 1));
}

/**
 * Check if a week key is the current week
 * @param {string} weekOfKey Week key in yyyy-MM-dd format
 * @returns {boolean} Whether the week is the current week
 */
export function isCurrentWeek(weekOfKey) {
  return weekOfKey === getCurrentWeekKey();
}

/**
 * Parse a week key string to a Date object
 * @param {string} weekOfKey Week key in yyyy-MM-dd format
 * @returns {Date} The parsed date
 */
export function parseWeekKey(weekOfKey) {
  return parseISO(weekOfKey);
}

/**
 * Check if a date is the first day of its week
 * @param {Date} date The date to check (defaults to now)
 * @param {number} weekStartsOn Day the week starts on (0=Sun, 1=Mon, etc. Default: 1)
 * @returns {boolean} Whether the date is the first day of the week
 */
export function isFirstDayOfWeek(date = new Date(), weekStartsOn = 1) {
  return date.getDay() === weekStartsOn;
}
